import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { FollowersDialog } from '@/components/FollowersDialog';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { QrCode, Radio, LogOut, Mail, Phone, Shield, Sparkles, MapPin, Users, Trash2, AlertTriangle, Share2, Camera, Pencil } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { timeAgo, CATEGORY_META, fileToDataUrl } from '@/lib/utils';
import { uploadImage } from '@/lib/mediaUpload';
import { toast } from 'sonner';
import { ShareAppDialog } from '@/components/ShareAppDialog';

function FollowingSection() {
  const [follows, setFollows] = useState(null);

  useEffect(() => {
    api.get('/follows/me').then((r) => setFollows(r.data)).catch(() => setFollows({ users: [], venues: [] }));
  }, []);

  const total = (follows?.users?.length || 0) + (follows?.venues?.length || 0);
  if (!follows || total === 0) return null;

  return (
    <GlassCard className="p-4" data-testid="profile-following-section">
      <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
        Following <span className="text-white/50 text-sm">· {total}</span>
      </p>
      {follows.users.length > 0 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
          {follows.users.map((u) => (
            <Link key={u.id} to={`/user/${u.id}`} className="flex w-16 flex-col items-center gap-1" data-testid={`following-user-${u.id}`}>
              <div className="relative">
                <Avatar className="h-12 w-12 border border-white/15">
                  <AvatarImage src={u.avatar_url} />
                  <AvatarFallback className="bg-[#F6B24A]/25 text-[#F6B24A]">{(u.display_name || 'U').slice(0, 1)}</AvatarFallback>
                </Avatar>
                {u.live_room && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-red-600 px-1 text-[8px] font-bold text-white">LIVE</span>}
              </div>
              <span className="w-full truncate text-center text-[10px] text-white/70">{u.display_name}</span>
            </Link>
          ))}
        </div>
      )}
      {follows.venues.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {follows.venues.map((v) => (
            <Link key={v.id} to={`/venue/${v.id}`} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5" data-testid={`following-venue-${v.id}`}>
              <MapPin className="h-3.5 w-3.5 shrink-0 text-[#F6B24A]" />
              <span className="min-w-0 flex-1 truncate text-sm text-white">{v.name}</span>
              <span className="text-[11px] text-white/50">{CATEGORY_META[v.category]?.label || v.category} · {v.city}, {v.state}</span>
            </Link>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

export default function ProfilePage() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delPassword, setDelPassword] = useState('');
  const [delConfirm, setDelConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const avatarFileRef = React.useRef();
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followersCount, setFollowersCount] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/follow/status/user/${user.id}`)
      .then((r) => setFollowersCount(r.data.followers))
      .catch(() => {});
  }, [user?.id]);

  const openEdit = () => {
    setEditName(user?.display_name || '');
    setEditBio(user?.bio || '');
    setEditOpen(true);
  };

  const saveProfile = async () => {
    const name = editName.trim();
    if (!name) {
      toast.error('Display name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch('/users/me', { display_name: name, bio: editBio.trim() });
      await refresh();
      toast.success('Profile updated');
      setEditOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const onAvatarFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await fileToDataUrl(f, 400, 0.8);
      const url = await uploadImage(dataUrl);
      await api.patch('/users/me', { avatar_url: url });
      await refresh();
      toast.success('Profile photo updated');
    } catch {
      toast.error('Could not update photo');
    }
    e.target.value = '';
  };

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get(`/users/${user.id}`),
      api.get('/users/me/checkins', { params: { limit: 20 } }),
    ]).then(([s, c]) => {
      setStats(s.data);
      setCheckins(c.data);
    });
  }, [user]);

  const isLive = !!user?.live_at_venue_id;

  const stopLive = async () => {
    await api.post('/golive/stop');
    toast.success('Live status cleared');
    refresh();
  };

  const handleDeleteAccount = async () => {
    if (delConfirm.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    if (!delPassword) {
      toast.error('Enter your password');
      return;
    }
    try {
      setDeleting(true);
      await api.delete('/users/me', { data: { password: delPassword, confirm: delConfirm.trim().toUpperCase() } });
      toast.success('Your account has been permanently deleted.');
      setDeleteOpen(false);
      // Clear local auth and route to splash
      logout();
      nav('/auth/login', { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to delete account.';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <Logo size={96} withText={false} />
          <Button variant="ghost" onClick={() => { logout(); nav('/auth/login'); }} className="text-white/80 hover:bg-white/10" data-testid="profile-logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Log out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-4">
        <GlassCard className="p-5">
          <div className="flex items-center gap-4">
            <div className={isLive ? 'th-pulse-ring' : ''}>
              <button
                type="button"
                onClick={() => avatarFileRef.current?.click()}
                className="group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B24A]/70"
                aria-label="Change profile picture"
                data-testid="profile-avatar-edit-button"
              >
                <Avatar className="h-16 w-16 ring-2 ring-[#F6B24A]/70 ring-offset-2 ring-offset-[#07070A]">
                  <AvatarImage src={user.avatar_url} />
                  <AvatarFallback className="bg-[#F6B24A]/30 text-[#F6B24A] text-lg font-semibold">{(user.display_name || 'U').slice(0, 1)}</AvatarFallback>
                </Avatar>
                <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#F6B24A] text-[#1A1208] shadow transition-transform group-hover:scale-110">
                  <Camera className="h-3 w-3" />
                </span>
              </button>
              <input ref={avatarFileRef} type="file" accept="image/*" hidden onChange={onAvatarFile} data-testid="profile-avatar-input" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <button onClick={openEdit} className="text-left" aria-label="Edit profile name" data-testid="profile-display-name">
                  <h1 className="font-display text-2xl font-semibold text-white transition-colors hover:text-[#F6B24A]" style={{ fontFamily: 'Space Grotesk' }}>{user.display_name}</h1>
                </button>
                <button
                  onClick={openEdit}
                  className="rounded-full p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-[#F6B24A]"
                  aria-label="Edit profile"
                  data-testid="profile-edit-button"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-white/60">{user.email}</p>
              <button onClick={() => setFollowersOpen(true)} className="mt-0.5 text-sm text-white/60 transition-colors hover:text-[#F6B24A]" data-testid="profile-followers-count">
                <b className="text-white">{followersCount ?? '—'}</b> follower{followersCount === 1 ? '' : 's'}
              </button>
              {user.bio && <p className="mt-1 text-sm text-white/75" data-testid="profile-bio">{user.bio}</p>}
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge className="bg-white/10 text-white/80 border-white/10" data-testid="profile-role-badge">{user.role}</Badge>
                {isLive && (
                  <Badge className="bg-red-500/15 text-red-200 border-red-500/30 inline-flex items-center gap-1" data-testid="profile-live-badge">
                    <span className="inline-block h-2 w-2 rounded-full bg-red-500 th-live-dot" /> Live
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button onClick={() => nav('/profile/qr')} className="h-11 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="profile-show-qr-button">
              <QrCode className="mr-2 h-4 w-4" /> My QR
            </Button>
            {isLive ? (
              <Button onClick={stopLive} variant="outline" className="h-11 rounded-xl border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25" data-testid="profile-stop-live-button">
                Stop Live
              </Button>
            ) : (
              <Button onClick={() => nav('/profile/go-live')} variant="outline" className="h-11 rounded-xl border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/15" data-testid="profile-go-live-button">
                <Radio className="mr-2 h-4 w-4" /> Go Live
              </Button>
            )}
          </div>
        </GlassCard>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="border-white/10 bg-[#101015] sm:max-w-md" data-testid="profile-edit-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Profile</DialogTitle>
              <DialogDescription className="text-white/60">Update your display name and bio</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-white/80">Display name</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={60}
                  className="mt-1.5 border-white/12 bg-white/5 text-white"
                  data-testid="profile-edit-name-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Bio <span className="text-white/40">({editBio.length}/280)</span></Label>
                <Textarea
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value.slice(0, 280))}
                  rows={3}
                  placeholder="Tell the nightlife world about yourself…"
                  className="mt-1.5 border-white/12 bg-white/5 text-white"
                  data-testid="profile-edit-bio-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)} className="border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="profile-edit-cancel">Cancel</Button>
              <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#F6B24A] font-semibold text-[#1A1208] hover:bg-[#FFC065]" data-testid="profile-edit-save">
                {savingProfile ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <FollowersDialog userId={user.id} open={followersOpen} onOpenChange={setFollowersOpen} />

        <div className="grid grid-cols-3 gap-3">
          <GlassCard className="p-3 text-center">
            <p className="text-2xl font-bold text-white" data-testid="profile-stat-checkins">{stats?.checkins_count ?? 0}</p>
            <p className="text-[11px] uppercase tracking-wider text-white/60">Check-ins</p>
          </GlassCard>
          <GlassCard className="p-3 text-center">
            <p className="text-2xl font-bold text-white" data-testid="profile-stat-posts">{stats?.posts_count ?? 0}</p>
            <p className="text-[11px] uppercase tracking-wider text-white/60">Posts</p>
          </GlassCard>
          <GlassCard className="p-3 text-center">
            <p className="text-2xl font-bold text-[#F6B24A]">Free</p>
            <p className="text-[11px] uppercase tracking-wider text-white/60">Profile</p>
          </GlassCard>
        </div>

        <FollowingSection />

        {user.role === 'admin' && (
          <GlassCard className="p-4 border-[#2DE2E6]/30" >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Admin Tools</p>
                <p className="text-xs text-white/70">Manage venues, view stats.</p>
              </div>
              <Button onClick={() => nav('/admin')} className="rounded-xl bg-[#2DE2E6]/15 border border-[#2DE2E6]/30 text-[#2DE2E6] hover:bg-[#2DE2E6]/20" variant="outline" data-testid="profile-admin-link">
                <Shield className="mr-2 h-4 w-4" /> Admin
              </Button>
            </div>
          </GlassCard>
        )}

        <GlassCard className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {user.role === 'venue_owner' ? 'My Subscription' : 'For Venue Partners'}
              </p>
              <p className="text-xs text-white/70 truncate">
                {user.role === 'venue_owner' ? 'Manage your venue plan & payments.' : 'List your venue on Thirrsty — first month free.'}
              </p>
            </div>
            <Button
              onClick={() => nav(user.role === 'venue_owner' || user.role === 'admin' ? '/profile/subscription' : '/pricing')}
              className="rounded-xl bg-[#F6B24A]/15 border border-[#F6B24A]/30 text-[#F6B24A] hover:bg-[#F6B24A]/20 shrink-0"
              variant="outline"
              data-testid="profile-subscription-link"
            >
              {user.role === 'venue_owner' || user.role === 'admin' ? 'Manage' : 'View Plans'}
            </Button>
          </div>
        </GlassCard>

        <section>
          <h2 className="mb-2 text-xs uppercase tracking-[0.18em] text-white/60">Recent check-ins</h2>
          {checkins.length === 0 ? (
            <GlassCard className="p-4 text-center text-white/60 text-sm">No check-ins yet — visit a venue!</GlassCard>
          ) : (
            <div className="space-y-2">
              {checkins.map((c) => (
                <button
                  key={c.id}
                  onClick={() => c.venue && nav(`/venue/${c.venue_id}`)}
                  className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:border-white/20"
                  data-testid={`profile-checkin-${c.id}`}
                >
                  <img
                    src={c.venue?.image_url}
                    onError={(e) => { e.currentTarget.src = '/logo-hero.png'; }}
                    className="h-10 w-12 rounded-md object-cover"
                    alt=""
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">{c.venue?.name || 'Unknown'}</p>
                    <p className="text-xs text-white/60">{c.venue?.city}, {c.venue?.state} · {CATEGORY_META[c.venue?.category]?.label}</p>
                  </div>
                  <p className="text-xs text-white/50">{timeAgo(c.created_at)} ago</p>
                </button>
              ))}
            </div>
          )}
        </section>

        <GlassCard className="p-4 text-sm text-white/70">
          <p className="font-display text-white font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Need help?</p>
          <div className="flex items-center gap-3 flex-wrap">
            <a href="tel:+14105047466" className="inline-flex items-center gap-2 text-[#F6B24A] hover:underline" data-testid="profile-call-link">
              <Phone className="h-4 w-4" /> (410) 504-7466
            </a>
            <button onClick={() => nav('/contact')} className="inline-flex items-center gap-2 text-[#F6B24A] hover:underline" data-testid="profile-contact-link">
              <Mail className="h-4 w-4" /> Contact Us
            </button>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F6B24A]/15 text-[#F6B24A]">
              <Share2 className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-white font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Invite friends to Thirrsty</p>
              <p className="mt-1 text-xs text-white/65">Send the app to your crew — they get free profiles + QR discounts at every venue.</p>
              <ShareAppDialog
                trigger={
                  <Button
                    variant="outline"
                    className="mt-3 rounded-xl border-[#F6B24A]/40 bg-[#F6B24A]/10 text-[#F6B24A] hover:bg-[#F6B24A]/20 hover:text-[#F6B24A]"
                    data-testid="profile-share-app-button"
                  >
                    <Share2 className="mr-2 h-4 w-4" /> Share the app
                  </Button>
                }
              />
            </div>
          </div>
        </GlassCard>

        <section data-testid="profile-danger-zone">
          <h2 className="mb-2 text-xs uppercase tracking-[0.18em] text-red-300/80">Danger zone</h2>
          <GlassCard className="border border-red-500/25 bg-red-500/[0.04] p-4">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-red-500/15 text-red-300">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-white font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Delete account</p>
                <p className="mt-1 text-xs text-white/65">
                  Permanently remove your account, posts, stories, check-ins, and likes. Any venues you own will be released for re-claim. This action cannot be undone.
                </p>
                <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) { setDelPassword(''); setDelConfirm(''); } }}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="mt-3 rounded-xl border-red-400/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 hover:text-red-100"
                      data-testid="profile-delete-account-button"
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete my account
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0E0E14] border-red-500/30 text-white" data-testid="delete-account-dialog">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-red-200">
                        <AlertTriangle className="h-5 w-5" /> Delete your Thirrsty account?
                      </DialogTitle>
                      <DialogDescription className="text-white/70">
                        This will permanently delete <span className="font-semibold text-white">{user?.email}</span> and all your content. Owned venues are released for re-claim. This cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="delete-pwd" className="text-white/80">Confirm your password</Label>
                        <Input
                          id="delete-pwd"
                          type="password"
                          autoComplete="current-password"
                          value={delPassword}
                          onChange={(e) => setDelPassword(e.target.value)}
                          placeholder="Your password"
                          className="bg-white/5 border-white/15 text-white"
                          data-testid="delete-account-password-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="delete-confirm" className="text-white/80">
                          Type <span className="font-mono text-red-300">DELETE</span> to confirm
                        </Label>
                        <Input
                          id="delete-confirm"
                          value={delConfirm}
                          onChange={(e) => setDelConfirm(e.target.value)}
                          placeholder="DELETE"
                          autoComplete="off"
                          className="bg-white/5 border-white/15 text-white font-mono uppercase"
                          data-testid="delete-account-confirm-input"
                        />
                      </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => setDeleteOpen(false)}
                        disabled={deleting}
                        className="text-white/80 hover:bg-white/10"
                        data-testid="delete-account-cancel-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={deleting || delConfirm.trim().toUpperCase() !== 'DELETE' || !delPassword}
                        className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                        data-testid="delete-account-confirm-button"
                      >
                        {deleting ? 'Deleting…' : 'Permanently delete'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </GlassCard>
        </section>
      </main>
    </div>
  );
}
